import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';

import {
  Client,
  GuidDictionaryItem,
  RoleSimpleDataOut,
  UserDataIn,
  UserDataOut,
  UserStatus
} from '../../../../api/api-reference';

@Component({
  selector: 'app-team-member-setup',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    RouterLink,
    FormsModule,
    NgSelectModule,
  ],
  templateUrl: './team-member-setup.component.html',
  styleUrl: './team-member-setup.component.scss',
})
export class TeamMemberSetupComponent implements OnInit {
  memberId: string = '';
  roles: RoleSimpleDataOut[] = [];
  regions: GuidDictionaryItem[] = [];
  areas: any[] = [];
  editAreas: any[] = [];
  region: string | null = '';
  data: UserDataOut = new UserDataOut();
  edit: boolean = false;
  selectedRegions: GuidDictionaryItem[] = [];
  selectedDistricts: any[] = [];
  tempRegion: any = null;
  tempDistrict: any = null;
  allRegions: boolean = false;
  userStatus = UserStatus;

  constructor(
    private cd: ChangeDetectorRef,
    private toastr: ToastrService,
    public router: Router,
    public route: ActivatedRoute,
    private client: Client,
  ) {}

  ngOnInit(): void {
    const state = history.state;
    if (state && state.userData) {
      this.memberId = state.userData;
    }

    if (this.memberId) this.getUser();
    this.getRoles();
  this.client.getAllRegions().subscribe({
    next: (response) => {
      this.regions = response ?? [];
      if (this.memberId) {
        this.getUser();
      }
    },
    error: (err) => this.toastr.error(err)
  });
}

  getRoles() {
    this.client.getAllRolesForOptions().subscribe({
      next: (response) => {
        this.roles = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getUser() {
    this.client.getUser(this.memberId).subscribe({
      next: (response) => {
        this.data = response ?? [];
        this.edit = true;
        this.allRegions = this.data.allRegions ?? false;
        // 1. Populate selectedRegions based on the IDs returned by the API.
      if (this.data.regionIds && this.data.regionIds.length > 0) {
        this.selectedRegions = this.regions.filter(reg =>
          this.data.regionIds?.includes(reg.value!)
        );

        // 2. For each of those regions, we need to fetch the corresponding areas (districts)
        //  to populate the 'areas' list for the dropdown and 'selectedDistricts' for the tags.
        this.data.regionIds.forEach(regId => {
          this.getAreasByRegion(regId);
        });
      }

      // 3. Refresh the dropdown display (if only one remains, it will be automatically selected)
      this.updateTempValues();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  updateTempValues() {
    setTimeout(() => {
      this.tempRegion = this.selectedRegions.length === 1 ? this.selectedRegions[0].value : null;
      this.tempDistrict = this.selectedDistricts.length === 1 ? this.selectedDistricts[0].value : null;
      this.cd.detectChanges();
    }, 0);
  }

  getRegions() {
    this.client.getAllRegions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  updateRoleId(roleId: string) {
    this.data.roleId = roleId;
  }

  onSubmit() {
    let areasGuid: string[] | null = null;

    // If 'All Regions' is selected, areas remains null
    if (!this.allRegions) {
      if (this.edit) {
        areasGuid =
          this.editAreas.length === 0
            ? null
            : this.editAreas
                .filter((area) => area.value !== undefined)
                .map((area) => area.value!);
      } else {
        areasGuid =
          this.selectedDistricts.length === 0
            ? null
            : this.selectedDistricts
                .filter((area) => area.value !== undefined)
                .map((area) => area.value!);
      }
    }

    const userDataIn = Object.assign(new UserDataIn(), {
      id: this.data.hesId,
      firstName: this.data.firstName,
      middleName: this.data.middleName,
      lastName: this.data.lastName,
      email: this.data.email,
      role: this.data.roleId,
      address: this.data.address,
      phoneNumber: this.data.phoneNumber,
      image: this.data.image,
      region: this.data.region,
      areas: areasGuid,
      allRegions: this.allRegions,
      regions: this.selectedRegions.map(r => r.value!),
    });

    this.client.saveUser(userDataIn).subscribe({
      next: () => {
        this.edit = false;
        this.toastr.success('Successfully saved. Re-login to see updated changes.');
        this.router.navigate(['/settings/team/members']);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  selectedRegion(regionValue: string) {
    if (!regionValue) return;

    const regionObj = this.regions.find(r => r.value === regionValue);
    const alreadySelected = this.selectedRegions.some(r => r.value === regionValue);

    if (regionObj && !alreadySelected) {
      this.selectedRegions = [...this.selectedRegions, regionObj];
      this.getAreasByRegion(regionValue);
    }

    setTimeout(() => {
      this.tempRegion = null;
      this.cd.detectChanges();
    }, 0);
  }

  removeRegion(deletedRegion: GuidDictionaryItem): void {
    const regionId = deletedRegion.value;

    // 1. Ukloni region iz selektovanih
    this.selectedRegions = this.selectedRegions.filter(r => r.value !== regionId);

    // 2. Ukloni iz selektovanih distrikta sve one koji pripadaju ovom regionu
    this.selectedDistricts = this.selectedDistricts.filter(
      (d: any) => d.parentRegionId !== regionId
    );

    this.editAreas = this.editAreas.filter(
      (d: any) => d.parentRegionId !== regionId
    );

    // 3. Ukloni te distrikte i iz liste ponuđenih opcija (dropdown)
    this.areas = this.areas.filter(
      (a: any) => a.parentRegionId !== regionId
    );

    this.tempDistrict = this.selectedDistricts.length === 1 ? this.selectedDistricts[0].value : null;
    this.tempRegion = this.selectedRegions.length === 1 ? this.selectedRegions[0].value : null;
  }

getAreasByRegion(regionId: string) {
  this.client.getAllAreaByRegion(regionId).subscribe({
    next: (response) => {
      const newAreas = response ?? [];

      // Cast-ujemo u any da bismo mogli da dodamo parentRegionId bez greške
      const areasWithParent = newAreas.map((area: any) => ({
        ...area,
        parentRegionId: regionId
      }));

      const uniqueNewAreas = areasWithParent.filter(
        newArea => !this.areas.some(existing => existing.value === newArea.value)
      );
      this.areas = [...this.areas, ...uniqueNewAreas];

      if (this.edit && this.data?.areas) {
        const matchingDistricts = areasWithParent.filter(area =>
          this.data.areas?.includes(area.value!) &&
          !this.selectedDistricts.some(sd => sd.value === area.value)
        );

        this.selectedDistricts = [...this.selectedDistricts, ...matchingDistricts];
        this.editAreas = [...this.selectedDistricts];
      }

      this.updateTempValues();
    },
    error: (err) => this.toastr.error(err),
  });
}

selectedDistrict(selectedValue: string) {
  if (!selectedValue) return;

  const selectedArea = this.areas.find(area => area.value === selectedValue);
  const areaExists = this.selectedDistricts.some(
    area => area.value?.trim().toLowerCase() === selectedValue.trim().toLowerCase()
  );

  if (selectedArea && !areaExists) {
    this.selectedDistricts = [...this.selectedDistricts, selectedArea];
    this.editAreas = [...this.editAreas, selectedArea];
  }

  setTimeout(() => {
    this.tempDistrict = null;
    this.cd.detectChanges();
  }, 0);
}

  removeDistrict(deletedDistrict: GuidDictionaryItem): void {
    this.selectedDistricts = this.selectedDistricts.filter(
      d => d.value !== deletedDistrict.value
    );
    this.editAreas = this.editAreas.filter(
      d => d.value !== deletedDistrict.value
    );

    // 2. Ažuriraj temp varijablu za dropdown
    if (this.selectedDistricts.length === 0) {
      this.tempDistrict = null;
    } else if (this.selectedDistricts.length === 1) {
      this.tempDistrict = this.selectedDistricts[0].value;
    } else {
      this.tempDistrict = null; // Više od jednog - placeholder
    }

  this.cd.detectChanges();
  }

  onAllRegionsChange(): void {
    if (this.allRegions) {
      this.selectedRegions = [];
      this.selectedDistricts = [];
      this.editAreas = [];
      this.tempRegion = null;
      this.tempDistrict = null;
      this.areas = [];
    }
    this.cd.detectChanges();
  }
}
